import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import {FieldError, get, Path, useFormContext} from "react-hook-form";
import {MANDATORY_ICON} from "../FormComponents";
import {ChangeEvent, ReactNode, useRef, useState} from "react";
import FileChip from "../../container/files/FileChip";

/**
 * Props for this component
 */
interface FileUploadProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  fieldId: Path<T>;
  disabled?: boolean;
  mandatory?: boolean;
  multiple?: boolean;
  acceptedTypes?: string;
}

/**
 * This component provides a form with a file download input. It was designed to be used inside a form panel. You may pass
 * the following props:
 *
 * @param label The text to show before the upload btn.
 * @param helpText Subtext that shows on top of the question
 * @param fieldId The ID of the field meant to store this file
 * @param disabled <i>Optional</i> A flag to mark the field as disabled
 * @param mandatory <i>Optional</i> A flag to mark the field as mandatory
 * @param multiple <i>Optional</i> A flag to make the field take more than one file
 * @param acceptedTypes <i>Optional</i> A list of accepted file types
 */
export default function FileUpload<T extends Record<string, any>>({
                                                                    label,
                                                                    helpText,
                                                                    fieldId,
                                                                    disabled = false,
                                                                    mandatory = false,
                                                                    multiple = false,
                                                                    acceptedTypes,
                                                                  }: FileUploadProps<T>) {
  const {register, setValue, formState: {errors}} = useFormContext<T>();
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  const fileError = get(errors, fieldId) as FieldError | undefined;

  const removeFile = (file: File) => {
    const updatedList = files.filter((item) => item !== file);
    setValue(fieldId, updatedList as any, {shouldValidate: true});
    setFiles(updatedList);
  }

  const addFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files ? Array.from(e.target.files) : [];
    const updatedList = [...files, ...chosen];
    if (fileInputRef.current) fileInputRef.current.value = "";
    setValue(fieldId, updatedList as any, {shouldValidate: true});
    setFiles(updatedList);
  }

  // RHF register
  const {
    ref: registerRef,
    ...fileInputRegister
  } = register(fieldId, {
    validate: (v) => {
      return (v.length === 0 && mandatory) ? `${label} is mandatory` : true
    }
  });


  return <Form.Group as={Row} className="mb-2" controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
      </Col>
    </Row>}
    <Row>
      <Form.Label column sm={2}>
        {label} {mandatory && <MANDATORY_ICON/>}
      </Form.Label>
      <Col sm={10} className={"file-upload"}>
        {/*Hide the form control here*/}
        <Form.Control
            type="file"
            multiple={multiple}
            accept={acceptedTypes}
            disabled={disabled}
            className="d-none"
            data-testid={`${fieldIdForDataTest}-input`}
            value={undefined}
            {...fileInputRegister}
            ref={(el: any) => {
              registerRef(el)
              fileInputRef.current = el;
            }}
            isInvalid={!!fileError}
            onChange={addFiles}
        />
        {(multiple || files.length === 0) &&
            <Button variant="primary"
                    disabled={disabled}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid={`${fieldIdForDataTest}-button`}>
              <i className="bi bi-upload me-2"/> Upload File
            </Button>}
        {/* Selected file chips */}
        {files.length > 0 && (<>
          {Array.from(files).map((file, index) => (
              <FileChip file={file} removeFile={removeFile} viewable key={`file-chip-${fieldIdForDataTest}-${index}`}/>
          ))}</>)}
        <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-error`}>
          {fileError?.message}
        </Form.Control.Feedback>
      </Col>
    </Row>
  </Form.Group>;
}